import { useState } from "react";
import {
  CanAccess,
  useGetIdentity,
  useGetList,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Navigate } from "react-router";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import type { Actor } from "../providers/commons/canAccess";
import {
  canAccessCarousel,
  getProjectScope,
} from "../providers/commons/canAccessCarousel";
import type {
  AppRole,
  BusinessArm,
  Project,
  Sale,
  SalesProject,
} from "../types";
import { GuardiasAdvisors } from "./GuardiasAdvisors";
import { GuardiasLimits } from "./GuardiasLimits";
import { GuardiasNotices } from "./GuardiasNotices";
import { GuardiasOfficeHours } from "./GuardiasOfficeHours";
import { GuardiasRoster } from "./GuardiasRoster";
import { GuardiasUnavailability } from "./GuardiasUnavailability";
import { projectAdvisors } from "./guardiasRules";
import { FIELD_CLASS } from "./guardiasShared";
import { Label } from "@/components/ui/label";

const PAGE = { page: 1, perPage: 1000 };
const SORT = { field: "id", order: "ASC" as const };

/**
 * Almikia Epic EL2 (TASK-074): "Guardias y asignación", one project at a time. It answers one
 * question: who is on guardia and who gets the leads. The role gates mirror the RLS of TASK-070
 * (`canAccessCarousel`): the Team Leader of THAT project, direccion and a bpo-scope lead_manager
 * edit; an asesora only sees (plus her own disponibilidad and her own 'no disponible' ranges);
 * everyone else, and the lider of another project, gets the standard no-access result. There
 * are no rankings, counts or comparisons between advisors and no presence data anywhere.
 */
export const GuardiasScreen = () => (
  <CanAccess
    resource="project_carousel"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <GuardiasContent />
  </CanAccess>
);

GuardiasScreen.path = "/guardias";

const GuardiasContent = () => {
  const translate = useTranslate();
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  const [chosenProject, setChosenProject] = useState("");

  const projects = useGetList<Project>("projects", {
    pagination: PAGE,
    sort: SORT,
  });
  const memberships = useGetList<SalesProject>("sales_projects", {
    pagination: PAGE,
    sort: SORT,
  });
  const sales = useGetList<Sale>("sales", { pagination: PAGE, sort: SORT });

  if (projects.error || memberships.error || sales.error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => {
          projects.refetch();
          memberships.refetch();
          sales.refetch();
        }}
      />
    );
  }
  if (isIdentityPending || !projects.data || !memberships.data || !sales.data) {
    return <ListCardsSkeleton rows={4} />;
  }

  // `identity?.role`: an identity without a role gets no access, never a crash.
  const role = identity?.role as AppRole | undefined;
  if (!role) {
    return <ListEmptyState title={translate("resources.guardias.no_access")} />;
  }
  const ownId: Identifier | undefined = identity?.id;
  const actor: Actor = {
    role,
    id: ownId,
    business_arm: (identity?.business_arm as BusinessArm | null) ?? null,
    ...getProjectScope(ownId, memberships.data),
  };

  const visibleProjects = projects.data.filter(
    (project) =>
      !project.archived &&
      canAccessCarousel(actor, {
        action: "list",
        resource: "project_carousel",
        record: { project_id: project.id },
      }),
  );
  const project =
    visibleProjects.find((item) => String(item.id) === chosenProject) ??
    visibleProjects[0];

  if (!project) {
    return (
      <ListEmptyState
        title={translate("resources.guardias.no_projects")}
        description={translate("resources.guardias.intro")}
      />
    );
  }

  const canEdit = canAccessCarousel(actor, {
    action: "edit",
    resource: "project_carousel",
    record: { project_id: project.id },
  });
  const advisors = projectAdvisors(project.id, memberships.data, sales.data);
  const section = { projectId: project.id, canEdit };

  return (
    <div className="mx-auto mt-2 max-w-3xl space-y-4 pb-8">
      <h1 className="px-1 text-2xl font-bold">
        {translate("resources.guardias.title")}
      </h1>
      <p className="px-1 text-[15px] text-muted-foreground">
        {translate("resources.guardias.intro")}
      </p>
      {!canEdit ? (
        <p className="px-1 text-[15px] text-muted-foreground">
          {translate("resources.guardias.readonly_note")}
        </p>
      ) : null}

      {visibleProjects.length > 1 ? (
        <div className="space-y-2 px-1">
          <Label htmlFor="guardias-project">
            {translate("resources.guardias.project_label")}
          </Label>
          <select
            id="guardias-project"
            className={FIELD_CLASS}
            value={String(project.id)}
            onChange={(e) => setChosenProject(e.target.value)}
          >
            {visibleProjects.map((item) => (
              <option key={item.id} value={String(item.id)}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p className="px-1 text-lg font-semibold">{project.name}</p>
      )}

      {/* Keyed by project so a switch never carries week or form state over. */}
      <div key={String(project.id)} className="space-y-4">
        <GuardiasRoster {...section} advisors={advisors} ownId={ownId} />
        <GuardiasAdvisors {...section} advisors={advisors} />
        <GuardiasOfficeHours {...section} />
        <GuardiasUnavailability
          canEdit={canEdit}
          advisors={advisors}
          ownId={ownId}
        />
        <GuardiasNotices {...section} ownId={ownId} />
        {canEdit ? <GuardiasLimits {...section} /> : null}
      </div>
    </div>
  );
};
